import React, { useState, useEffect } from 'react';
import { Upload, Trash2, FolderPlus, File, Folder, Download, AlertCircle, Check, X } from 'lucide-react';

const FileManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [showNewFolderForm, setShowNewFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);

  // Load files on component mount and path change
  useEffect(() => {
    loadFiles();
  }, [currentPath]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch('/api/pdf/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFiles(data.tree);
      } else {
        setError('Hiba történt a fájlok betöltése során');
      }
    } catch (error) {
      console.error('Error loading files:', error);
      setError('Hiba történt a fájlok betöltése során');
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (uploadFiles.length === 0) {
      setError('Válassz ki legalább egy fájlt');
      return;
    }
    
    setUploading(true);
    setError('');
    
    const formData = new FormData();
    uploadFiles.forEach(file => {
      formData.append('files', file);
    });
    
    if (currentPath) {
      formData.append('path', currentPath);
    }
    
    try {
      const response = await fetch('/api/files/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        setSuccess('Fájlok sikeresen feltöltve');
        setUploadFiles([]);
        setShowUploadForm(false);
        loadFiles();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a fájlok feltöltése során');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setError('Hiba történt a fájlok feltöltése során');
    } finally {
      setUploading(false);
    }
  };

  // Handle folder creation
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    
    if (!newFolderName.trim()) {
      setError('Add meg a mappa nevét');
      return;
    }
    
    try {
      const response = await fetch('/api/files/folder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: newFolderName,
          path: currentPath
        })
      });
      
      if (response.ok) {
        setSuccess('Mappa sikeresen létrehozva');
        setNewFolderName('');
        setShowNewFolderForm(false);
        loadFiles();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a mappa létrehozása során');
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      setError('Hiba történt a mappa létrehozása során');
    }
  };

  // Handle file/folder deletion
  const handleDelete = async (item) => {
    if (!window.confirm(`Biztosan törlöd: ${item.name}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/files/${item.type}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          path: item.path
        })
      });
      
      if (response.ok) {
        setSuccess(`${item.type === 'folder' ? 'Mappa' : 'Fájl'} sikeresen törölve`);
        loadFiles();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Hiba történt a törlés során');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('Hiba történt a törlés során');
    }
  };

  // Handle folder navigation
  const handleFolderClick = (folder) => {
    setCurrentPath(folder.path);
  };

  // Handle back navigation
  const handleGoBack = () => {
    if (currentPath) {
      const pathSegments = currentPath.split('/').filter(Boolean);
      const parentPath = pathSegments.slice(0, -1).join('/');
      setCurrentPath(parentPath);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get current folder files
  const getCurrentFolderItems = () => {
    if (!currentPath) {
      return files;
    }
    
    const pathSegments = currentPath.split('/').filter(Boolean);
    let currentItems = files;
    
    for (const segment of pathSegments) {
      const folder = currentItems.find(item => item.type === 'folder' && item.name === segment);
      if (folder && folder.children) {
        currentItems = folder.children;
      } else {
        return [];
      }
    }
    
    return currentItems;
  };

  // Get breadcrumb path
  const getBreadcrumbPath = () => {
    if (!currentPath) {
      return [{ name: 'Gyökér', path: '' }];
    }
    
    const segments = currentPath.split('/').filter(Boolean);
    const breadcrumbs = [{ name: 'Gyökér', path: '' }];
    
    let path = '';
    for (const segment of segments) {
      path = path ? `${path}/${segment}` : segment;
      breadcrumbs.push({ name: segment, path });
    }
    
    return breadcrumbs;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentItems = getCurrentFolderItems();
  const breadcrumbs = getBreadcrumbPath();

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Fájl Kezelő</h2>
        <p className="text-gray-600">Fájlok és mappák kezelése</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md flex items-center">
          <Check className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <div className="mb-4 flex items-center space-x-2 text-sm text-gray-600">
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path}>
            <button
              onClick={() => setCurrentPath(crumb.path)}
              className={`hover:text-blue-600 ${
                index === breadcrumbs.length - 1 ? 'font-medium text-gray-900' : ''
              }`}
            >
              {crumb.name}
            </button>
            {index < breadcrumbs.length - 1 && <span>/</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex space-x-2">
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Upload className="h-4 w-4 mr-2" />
          Fájl Feltöltés
        </button>
        
        <button
          onClick={() => setShowNewFolderForm(!showNewFolderForm)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <FolderPlus className="h-4 w-4 mr-2" />
          Új Mappa
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-lg font-medium mb-4">Fájl Feltöltés</h3>
          
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Válassz fájlokat
              </label>
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-sm text-gray-500 mt-1">
                Csak PDF fájlok engedélyezettek
              </p>
            </div>

            {uploadFiles.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Kiválasztott fájlok:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {uploadFiles.map((file, index) => (
                    <li key={index} className="flex items-center">
                      <File className="h-4 w-4 mr-2" />
                      {file.name} ({formatFileSize(file.size)})
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex space-x-2">
              <button
                type="submit"
                disabled={uploading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                {uploading ? 'Feltöltés...' : 'Feltöltés'}
              </button>
              
              <button
                type="button"
                onClick={() => setShowUploadForm(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-2" />
                Mégse
              </button>
            </div>
          </form>
        </div>
      )}

      {/* New Folder Form */}
      {showNewFolderForm && (
        <div className="mb-6 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-lg font-medium mb-4">Új Mappa Létrehozása</h3>
          
          <form onSubmit={handleCreateFolder} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mappa neve
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Új mappa neve..."
                required
              />
            </div>

            <div className="flex space-x-2">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <Check className="h-4 w-4 mr-2" />
                Létrehozás
              </button>
              
              <button
                type="button"
                onClick={() => setShowNewFolderForm(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-4 w-4 mr-2" />
                Mégse
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Files and Folders List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Fájlok és Mappák
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Jelenlegi mappa tartalma
          </p>
        </div>
        
        <div className="border-t border-gray-200">
          {currentItems.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              <Folder className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Üres mappa</h3>
              <p className="mt-1 text-sm text-gray-500">
                Ez a mappa még nem tartalmaz fájlokat vagy almappákat
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {currentItems.map((item) => (
                <div key={item.path} className="px-4 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex items-center">
                    {item.type === 'folder' ? (
                      <Folder className="h-5 w-5 text-blue-500 mr-3" />
                    ) : (
                      <File className="h-5 w-5 text-gray-400 mr-3" />
                    )}
                    <div>
                      <button
                        onClick={() => item.type === 'folder' ? handleFolderClick(item) : null}
                        className={`text-sm font-medium ${
                          item.type === 'folder' 
                            ? 'text-blue-600 hover:text-blue-900' 
                            : 'text-gray-900'
                        }`}
                      >
                        {item.name}
                      </button>
                      {item.type === 'file' && item.size && (
                        <p className="text-sm text-gray-500">{formatFileSize(item.size)}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDelete(item)}
                      className="text-red-600 hover:text-red-900"
                      title="Törlés"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileManager; 